/**
 * WidgetMainSessionPanel — the widget's MAIN session view shown IN PLACE inside
 * the Widget Studio (an overlay), instead of navigating away to
 * `/playground/chat/{sid}`. This is the web equivalent of what the app does when
 * you tap a widget's "Chat with widget" button: a full session view with the
 * widget cover on top + the chat thread + an input bar — keeping the widget's
 * context, with a Back that returns to the editor (URL stays on
 * `/playground/widget/studio/:widgetId`).
 *
 * Reuses the canonical "app path" quartet (mirrors App.jsx / ChatPage wiring):
 *   useSessionStore (state) + useSessionEventStream (live + WAL replay) +
 *   api.dispatchExec (send) + SessionView (thread + input). The cover is
 *   WidgetChatCover, fed a synthesized widget_main session (no detail fetch).
 */
import { useCallback, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { useSessionStore } from '../../hooks/useSessionStore';
import { useSessionEventStream } from '../../hooks/useSessionEventStream';
import SessionView from '../SessionView';
import WidgetChatCover from './WidgetChatCover';

export default function WidgetMainSessionPanel({ sessionId, widgetId, instanceId, tag, ask, onClose }) {
  const store = useSessionStore();
  const viUserId = api.getViUserId?.() || null;
  const askSentRef = useRef(false);

  // Live stream (replays the session WAL on connect + streams new turns) → store.
  useSessionEventStream(viUserId, sessionId, { onEvent: store.processEvent });

  // Seed the persisted history (DB snapshot) + mark this session active so the
  // stream's events route to it. Set active synchronously first so events that
  // arrive before the detail fetch resolves aren't mis-routed.
  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    store.setActiveSessionId?.(sessionId);
    (async () => {
      try {
        const detail = await api.getSession(sessionId);
        if (!alive || !detail) return;
        store.loadSession({ ...detail, id: detail.id || detail.session_id || sessionId });
      } catch {
        if (alive) store.switchSession?.(sessionId);
      }
    })();
    return () => { alive = false; };
    // store methods are stable (useCallback in the hook).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Auto-send the opening line once (the widget button's prompt), after the
  // history seed kicks off — mirrors the app's initialPrompt auto-send.
  useEffect(() => {
    if (!sessionId || !ask || askSentRef.current) return;
    askSentRef.current = true;
    api.dispatchExec({ prompt: ask, sessionId }).catch((e) =>
      console.warn('[widget-main] opening send failed:', e));
  }, [sessionId, ask]);

  const onSendMessage = useCallback((text) => {
    const t = (text || '').trim();
    if (!t || !sessionId) return;
    api.dispatchExec({ prompt: t, sessionId }).catch((e) =>
      console.warn('[widget-main] send failed:', e));
  }, [sessionId]);

  // Synthetic session for the cover — WidgetChatCover only reads session_type +
  // context.widget_use_contract, so no detail fetch is needed for it.
  const coverSession = {
    session_type: 'widget_main',
    context: { widget_use_contract: { widget_id: widgetId, widget_instance_id: instanceId, tag } },
  };

  return (
    // absolute inset-0 → fills the iPhone preview FACE (PhonePreview's relative,
    // scaled phone screen), NOT the browser window. Mounted as a child of
    // PhonePreview so it lives inside the phone frame like the app's screen.
    <div style={{
      position: 'absolute', inset: 0, zIndex: 30, background: '#fff',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Widget cover on top (scrolls with nothing — a fixed hero band). */}
      <div style={{ flexShrink: 0 }}>
        <WidgetChatCover session={coverSession} />
      </div>
      {/* Chat thread + input below. SessionView is absolute inset-0, so it fills
          this relative region; its own Back closes the panel → back to editor. */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <SessionView
          cards={store.cards}
          orderedCardIds={store.orderedCardIds}
          tasks={store.tasks}
          sessionId={sessionId}
          onSendMessage={onSendMessage}
          onCardAction={() => {}}
          onBack={onClose}
        />
      </div>
    </div>
  );
}
